import { DEFAULT_RETRY } from '../api/queryDefaults'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

/**
 * The server-state client.
 *
 * `useState` with an initialiser rather than a module-level `new QueryClient()`:
 * a module singleton is shared across every test in a file and across every
 * render of a StrictMode double-mount, so one test's cache leaks into the next.
 * This gives each mounted provider its own.
 *
 * `retry: 1` — one retry, not three. A failed request should reach `<Async>`'s
 * error state while the user is still looking at the screen; three retries with
 * backoff is roughly ten seconds of a spinner that resolves to a failure.
 *
 * `refetchOnWindowFocus: false` — this app shows money. A balance changing
 * because the user alt-tabbed back is alarming rather than helpful, and the
 * sync story is explicit (a stale state with the date it is from).
 */
export function QueryProvider({ children }: { children: ReactNode }) {
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { staleTime: 30_000, retry: DEFAULT_RETRY, refetchOnWindowFocus: false },
        },
      }),
  )
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>
}
